import { useEffect, useState, useRef } from 'react';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import FileDropZone from '../components/FileDropZone';

// ── 전자결재 ─────────────────────────────────────────────────────────────
// 탭 구성: 결재완료 / 결재대기(내가 결재할 문서 + 내가 상신해 진행중인 문서) /
// 회람·참조 / 결재반려(내가 기안한 반려문서 + 내가 반려처리한 문서) / 기안하기 / 양식관리.
// 상신함은 결재대기 탭에 통합했고, 위임관리는 화면에서 뺐습니다(데이터·API는 유지).

// 2026-09(15차) 요청 — "양식관리" 탭과 그 기능(양식 목록/양식추가/서식편집 등)을 통째로
// 환경설정 > 문서번호 화면 우측으로 이전했다(Settings.js의 FormsTab 참고). 이 화면에는
// 더 이상 양식관리 탭이 없고, 기안하기(DraftTab)가 양식을 고를 때 쓰는 forms 목록 조회
// (GET /approval/forms)만 그대로 남아있다.
const TABS = [
  { key:'done',      label:'결재완료' },
  { key:'pending',   label:'결재대기' },
  { key:'reference', label:'회람/참조' },
  { key:'rejected',  label:'결재반려' },
];

// 결재자 검색 선택기 — 이름/부서/직급으로 검색해서 한 명을 고른다(드롭다운 대신 검색형).
function UserSearchSelect({ users, value, onChange, placeholder = '결재자 검색 (이름·부서·직급)' }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = users.find(u => String(u.id) === String(value));
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const kw = q.trim().toLowerCase();
  const list = !kw ? users : users.filter(u =>
    `${u.name || ''} ${u.department || ''} ${u.position || ''}`.toLowerCase().includes(kw));
  const label = (u) => `${u.department || '-'} · ${u.position || '-'} · ${u.name}`;
  return (
    <div ref={wrapRef} style={{ position:'relative', flex:1, minWidth:0 }}>
      <input
        style={{ ...input, width:'100%', paddingRight: selected ? 32 : 14 }}
        placeholder={placeholder}
        value={open ? q : (selected ? label(selected) : '')}
        onFocus={() => { setOpen(true); setQ(''); }}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
      />
      {selected && !open && (
        <button type="button" title="선택 해제" onClick={() => onChange('')}
          style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#999', cursor:'pointer', fontSize:14 }}>✕</button>
      )}
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:50, background:'#fff', border:'1px solid #ddd', borderRadius:8, marginTop:4, maxHeight:220, overflowY:'auto', boxShadow:'0 6px 18px rgba(0,0,0,0.12)' }}>
          {list.length === 0 && <div style={{ padding:'10px 14px', fontSize:13, color:'#aaa' }}>검색 결과가 없습니다.</div>}
          {list.map(u => (
            <div key={u.id} onMouseDown={e => e.preventDefault()}
              onClick={() => { onChange(u.id); setOpen(false); setQ(''); }}
              style={{ padding:'8px 14px', fontSize:13, cursor:'pointer', background: String(u.id) === String(value) ? '#e8f4ff' : '#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = String(u.id) === String(value) ? '#e8f4ff' : '#fff'}>
              <b>{u.name}</b> <span style={{ color:'#888' }}>{u.department || '-'} · {u.position || '-'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 양식명을 "회 의 록"처럼 글자 사이를 띄운 공문서 제목 표기로 바꿔준다(회의록 템플릿의
// 기안 화면 상단 제목에 사용).
const spacedTitle = (name) => (name || '').split('').join(' ');

// 스타일 상수 — 인사관리(HRManagement.js)/계약관리(Contracts.js)와 동일한 값으로 통일
// (버튼/입력창/목록 표 폰트크기·색상·여백을 다른 메뉴와 맞춰 화면에서 이질감이 없도록 함).
const C = { primary:'#185FA5', border:'#e5e7eb', bg:'#f8fafc', text:'#333', sub:'#888' };
const btn = (bg, color='#fff') => ({ background:bg, color, border:'none', borderRadius:8, padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' });
const input = { padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };
const th = { padding:'10px 14px', fontSize:14, fontWeight:600, color:'#666', background:'#f8fafc', textAlign:'center', whiteSpace:'nowrap', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 14px', fontSize:14, color:C.text, borderTop:'1px solid #f0f0f0', textAlign:'center' };

function Section({ title, action, children }) {
  return (
    <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
      {(title || action) && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:16, fontWeight:600 }}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}
// 목록 정렬(오름차순/내림차순) — 컬럼명을 클릭할 때마다 토글된다. 다른 메뉴(계약관리/인사관리
// 등)의 목록 클릭 정렬과 동일한 방식으로 통일했다.
function useSort(list, defaultKey = null) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  let sorted = list;
  if (sortKey) {
    sorted = [...list].sort((a, b) => {
      let va = a ? a[sortKey] : null, vb = b ? b[sortKey] : null;
      if (va === null || va === undefined) va = '';
      if (vb === null || vb === undefined) vb = '';
      let cmp;
      const na = Number(va), nb = Number(vb);
      if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }
  return { sorted, sortKey, sortDir, toggleSort };
}
function SortTh({ label, sortKey, currentKey, currentDir, onSort, align }) {
  if (!sortKey) return <th style={{ ...th, textAlign: align || th.textAlign }}>{label}</th>;
  const active = currentKey === sortKey;
  return (
    <th style={{ ...th, textAlign: align || th.textAlign, cursor:'pointer', userSelect:'none', color: active ? C.primary : th.color }}
      title={active ? (currentDir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      onClick={() => onSort(sortKey)}>
      {label}
    </th>
  );
}
function EmptyRow({ span, text='표시할 문서가 없습니다.' }) {
  return <tr><td colSpan={span} style={{ ...td, textAlign:'center', color:'#aaa', padding:24 }}>{text}</td></tr>;
}
// headerExtra: 제목과 닫기(✕) 버튼 사이에 끼워넣을 부가 컨트롤(예: 문서번호 모달의 단일/분야별
// 체계 선택창). 지정하지 않으면 기존과 동일하게 제목-닫기 버튼만 표시된다.
function Modal({ title, onClose, children, width=520, headerExtra }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:14, width, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>{title}</h3>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {headerExtra}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#999', cursor:'pointer' }}>✕</button>
          </div>
        </div>
        <div style={{ padding:'16px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div style={{ marginBottom:12 }}><div style={{ fontSize:12, color:'#666', marginBottom:5, fontWeight:600 }}>{label}</div>{children}</div>;
}
const statusColor = (s) => ({
  '임시저장':{ bg:'#f1f5f9', c:'#888' }, '진행중':{ bg:'#fef3c7', c:'#b45309' },
  '승인':{ bg:'#dcfce7', c:'#16a34a' }, '반려':{ bg:'#fee2e2', c:'#dc2626' }, '회수':{ bg:'#f1f5f9', c:'#888' },
}[s] || { bg:'#f1f5f9', c:'#888' });
function StatusBadge({ s }) { const c = statusColor(s); return <span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:c.bg, color:c.c }}>{s}</span>; }
const roleColor = { '결재':{ bg:'#e8f4ff', c:C.primary }, '합의':{ bg:'#f3e8ff', c:'#7c3aed' }, '참조':{ bg:'#f1f5f9', c:'#888' } };

export default function ElectronicApproval() {
  const { user } = useAuth();
  const perm = usePermissions();
  const [tab, setTab] = useState(TABS[0].key);
  const [users, setUsers] = useState([]);
  const [forms, setForms] = useState([]);
  const [msg, setMsg] = useState('');
  const [openDocId, setOpenDocId] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false); // 우측 상단 "기안하기" 모달
  const [reloadKey, setReloadKey] = useState(0);
  // 문서번호 관리체계(구 📑 문서번호 버튼/DocRegistryModal)는 2026-09에 환경설정 > 문서번호
  // 화면으로 이전되었다. 이 화면에는 더 이상 관련 버튼/모달이 없다(양식추가 시 쓰는
  // DocRegPickerModal만 남아있다 — 그건 문서번호를 "고르는" 용도라 이전 대상이 아니다).
  // 2026-09(15차) — "양식관리" 탭 자체도 환경설정 > 문서번호 화면으로 이전되어, 여기서는
  // forms 목록을 기안하기(DraftTab)의 양식 선택 드롭다운 용도로만 조회한다.

  const loadUsers = () => API.get('/approval/users').then(r => setUsers(r.data)).catch(() => {});
  const loadForms = () => API.get('/approval/forms').then(r => setForms(r.data)).catch(() => {});
  useEffect(() => { loadUsers(); loadForms(); }, []);

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(''), 2500); };
  // 전체문서함 열람 권한 — 예전에는 "양식관리(form_manage)" 권한자와 동일하게 적용했는데,
  // 양식관리 기능이 환경설정으로 이전된 뒤에도 이 권한 키 자체는 그대로 유지해서 쓴다.
  const canViewAll = perm.hasDetail('e-approval', 'form_manage');

  const tabBtn = (t) => {
    return (
      <button key={t.key} onClick={() => setTab(t.key)}
        style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer',
          fontSize:14, fontWeight:600, color: tab===t.key ? C.primary : '#888',
          borderBottom: tab===t.key ? `2px solid ${C.primary}` : '2px solid transparent', marginBottom:-2, whiteSpace:'nowrap' }}>
        {t.label}
      </button>
    );
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>
      <div style={{ marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🖊️ 전자결재</h2>
          <button onClick={() => { loadForms(); loadUsers(); setDraftOpen(true); }} className="hdr-btn" style={btn(C.primary)}>✏️ 기안하기</button>
        </div>
        <div style={{ fontSize:13, color:C.sub, marginTop:4 }}>문서를 기안하고 결재선을 지정해 상신하며, 결재함에서 승인/반려를 처리합니다.</div>
      </div>

      {msg && <div style={{ marginBottom:12, padding:'9px 14px', background:'#e8f4ff', color:C.primary, borderRadius:8, fontSize:13, fontWeight:600 }}>{msg}</div>}

      <div style={{ display:'flex', flexWrap:'wrap', borderBottom:`1.5px solid ${C.border}`, marginBottom:18, background:'#fff', borderRadius:'10px 10px 0 0', padding:'0 8px' }}>
        {TABS.map(tabBtn)}
      </div>

      {tab === 'done'      && <DoneTab      key={reloadKey} user={user} canViewAll={canViewAll} showMsg={showMsg} openDocId={openDocId} setOpenDocId={setOpenDocId} />}
      {tab === 'pending'   && <PendingTab   key={reloadKey} user={user} canViewAll={canViewAll} showMsg={showMsg} openDocId={openDocId} setOpenDocId={setOpenDocId} />}
      {tab === 'reference' && <ReferenceTab key={reloadKey} user={user} canViewAll={canViewAll} showMsg={showMsg} openDocId={openDocId} setOpenDocId={setOpenDocId} />}
      {tab === 'rejected'  && <RejectedTab  key={reloadKey} user={user} canViewAll={canViewAll} showMsg={showMsg} openDocId={openDocId} setOpenDocId={setOpenDocId} />}

      {draftOpen && (
        <Modal title="✏️ 기안하기" onClose={() => setDraftOpen(false)} width={1180}>
          <DraftTab users={users} forms={forms} user={user} showMsg={showMsg}
            onSubmitted={(submitted) => { setDraftOpen(false); if (submitted) setTab('pending'); setReloadKey(k => k + 1); }} />
        </Modal>
      )}
    </div>
  );
}

// ── 문서번호 관리(체계) 모달 ────────────────────────────────────────────
// 2026-09 개편 — 전역 체계가 두 가지로 나뉜다.
//  - 단일체계: 조직 구분 없이 회사 전체가 구분(8개, 2글자 이니셜) 하나를 공유하고, 번호 맨
//    앞에는 관리자가 직접 정하는 회사공통 접두어가 붙는다.
//  - 분야별체계: 회사공용(NINE)/나인세이프티(NS)/나인케이스(NC) 조직별로 독립된 구분표(알파벳
//    1글자 접두어)를 각자 구성한다. 기본은 비어 있고 +버튼으로 직접 추가하며, "회사공통"으로
//    체크하면 단일체계 구분표를 그대로 복사해 올 수 있다.
// 구버전 DocNumberRuleModal(자유 카테고리+접두어)은 대체되었지만 백엔드 라우트/데이터는 남아
// 있어 예전에 그 방식으로 채번된 양식들의 doc_no는 그대로 유효하다.
// 문서번호 선택기(구 DocRegPickerModal, 팝업 모달)는 삭제되었다 — 2026-09(양식추가 재구성 2차)
// 요청("분야별선택버튼은 삭제")에 따라 별도 팝업 없이 FormsTab의 양식추가 모달 안에 대분류/
// 중분류 선택과 자동채번 미리보기를 바로 넣고, 추가를 누르면 문서번호 등록(doc_registry)과
// 양식 생성(approval_forms)이 한 번에 처리되어 "하나의 양식"으로 남는다. 자세한 내용은
// FormsTab 내부 주석 참고.

// 업로드 양식 빌더로 만든 커스텀 레이아웃을, 기안 작성 화면에서 실제로 값을 입력할 수 있는
// 표로 렌더링한다(양식관리의 FormLayoutEditorModal과 같은 레이아웃 JSON을 그대로 읽되, 여기서는
// 칸 구조를 수정하지 않고 isField 칸에만 실제 <input>/<textarea>를 그려 값만 입력받는다).
function FillableLayoutTable({ layout, fieldValues, setFieldValues }) {
  const colWidthsPct = layout?.colWidthsPct || [100];
  // 2026-09: 레이아웃이 다중 페이지({ pages: [{rows}, {rows}, ...] })로 저장될 수 있다.
  // 예전에 저장된 단일 페이지 형태({ rows })도 그대로 읽을 수 있도록 정규화한다.
  const pages = Array.isArray(layout?.pages) && layout.pages.length ? layout.pages : [{ rows: layout?.rows || [] }];
  const setVal = (key, v) => setFieldValues(fv => ({ ...fv, [key]: v }));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {pages.map((page, pi) => (
        <div key={pi}>
          {pages.length > 1 && (
            <div style={{ fontSize:12, fontWeight:600, color:C.sub, marginBottom:6 }}>— {pi + 1}페이지 —</div>
          )}
          <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
              <colgroup>{colWidthsPct.map((w, i) => <col key={i} style={{ width:`${w}%` }} />)}</colgroup>
              <tbody>
                {(page.rows || []).map((row, ri) => (
                  <tr key={ri} style={row.heightPx ? { height: row.heightPx } : undefined}>
                    {(row.cells || []).map((cell, ci) => {
                      if (cell.merged) return null;
                      const align = cell.align || 'left';
                      const cellStyle = {
                        border:'1px solid #ddd', padding:'6px 8px', verticalAlign:'middle',
                        textAlign: cell.isField ? 'left' : align,
                        fontWeight: cell.bold ? 700 : 400, fontSize: cell.fontSize || 12,
                      };
                      // 얇은 구분 공란(엑셀 행 높이 < 22px)은 패딩/글자크기를 줄여 엑셀과 같은 높이로 표시
                      if (row.heightPx && row.heightPx < 22) {
                        Object.assign(cellStyle, { padding:0, lineHeight:1, height:row.heightPx, overflow:'hidden',
                          fontSize: Math.max(1, Math.min(cell.fontSize || 12, Math.floor(row.heightPx * 0.75))) });
                      }
                      return (
                        <td key={ci} rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                          colSpan={cell.colSpan > 1 ? cell.colSpan : undefined} style={cellStyle}>
                          {cell.isField ? (
                            cell.fieldType === 'textarea' ? (
                              <textarea rows={2} value={fieldValues[cell.fieldKey] || ''} onChange={e => setVal(cell.fieldKey, e.target.value)}
                                style={{ width:'100%', border:'1px dashed #cbd5e1', borderRadius:4, padding:'4px 6px', fontSize:12, resize:'vertical', boxSizing:'border-box' }} />
                            ) : (
                              <input value={fieldValues[cell.fieldKey] || ''} onChange={e => setVal(cell.fieldKey, e.target.value)}
                                style={{ width:'100%', border:'1px dashed #cbd5e1', borderRadius:4, padding:'4px 6px', fontSize:12, boxSizing:'border-box' }} />
                            )
                          ) : (cell.text || '')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 기안하기
// ═══════════════════════════════════════════════════════════════
// 2026-09(양식 자동인식) — 업로드 양식에서 자동으로 찾은 라벨-옆칸 필드(fieldKey)들을
// 실제 로그인 사용자 정보/오늘 날짜/양식의 문서번호로 미리 채워 넣는다. 사용자는 그대로
// 두거나 FillableLayoutTable에서 직접 고쳐 쓸 수 있다(빈 값을 강제로 덮어쓰지 않음).
function collectLayoutFieldKeys(layout) {
  const keys = new Set();
  const pages = Array.isArray(layout?.pages) && layout.pages.length ? layout.pages : [{ rows: layout?.rows || [] }];
  pages.forEach(page => (page.rows || []).forEach(row => (row.cells || []).forEach(cell => {
    if (cell && cell.isField && cell.fieldKey) keys.add(cell.fieldKey);
  })));
  return keys;
}

function DraftTab({ users, forms, user, showMsg, onSubmitted }) {
  const blank = { form_id:'', title:'', content:'', amount:'', department: user?.department || '' };
  const [doc, setDoc] = useState(blank);
  const [lines, setLines] = useState([]); // { approver_id, approver_name, role } — 일반 양식용 자유 결재선
  const [pickUser, setPickUser] = useState('');
  const [pickRole, setPickRole] = useState('결재');
  // 회의록형 양식(template_type==='meeting_minutes') 전용 상태 —
  // 업무회람/업무협조 선택과, 양식에 지정된 결재라인(검토/검토/승인 등) 고정 슬롯별 결재자.
  const [circType, setCircType] = useState('');   // '' | '회람' | '협조'
  const [circDept, setCircDept] = useState('');
  const [depts, setDepts] = useState([]);
  const [slotUsers, setSlotUsers] = useState({}); // { stepIdx: userId }
  // 업로드 양식 빌더로 만든 커스텀 레이아웃(양식관리에서 표 구조를 편집/저장한 양식) 전용 —
  // 레이아웃의 입력란(fieldKey)별 입력값과, PDF 미리보기/다운로드 진행 상태.
  const [fieldValues, setFieldValues] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => { API.get('/approval/departments').then(r => setDepts(r.data)).catch(() => {}); }, []);

  const selectedForm = forms.find(f => String(f.id) === String(doc.form_id));
  const isMeetingTpl = selectedForm?.template_type === 'meeting_minutes';
  let customLayout = null;
  if (selectedForm?.layout_json) { try { customLayout = JSON.parse(selectedForm.layout_json); } catch { customLayout = null; } }
  let steps = null;
  if (selectedForm?.approval_steps) { try { steps = JSON.parse(selectedForm.approval_steps); } catch { steps = null; } }
  const reviewSteps = steps ? steps.slice(1) : null; // 첫 단계("작성")는 기안자 본인이라 제외

  // 양식을 바꾸면 결재선/입력란 관련 입력을 초기화한다(이전 양식 기준으로 지정한 결재자나
  // 입력값이 남아있지 않도록). 커스텀 레이아웃 양식이면, 라벨-옆칸으로 자동 인식된
  // 입력란(fieldKey)에 실제 값(문서번호/오늘날짜/로그인한 내 정보)을 바로 채워 넣어
  // 기안자가 문서번호나 자기 이름을 다시 입력할 필요가 없게 한다.
  useEffect(() => {
    setLines([]); setSlotUsers({}); setCircType(''); setCircDept('');
    if (customLayout) {
      const keys = collectLayoutFieldKeys(customLayout);
      const today = new Date().toLocaleDateString('ko-KR');
      const auto = {};
      // 자동입력 ID 9종(year/month/day/ymd_dot/form_no/company_name/form_spec/doc_no/printer)은
      // 서버가 해석한 값으로 채운다. 문서번호(doc_no)는 기록물번호라 발급 전에는 빈 값이다.
      if (keys.has('created_at')) auto.created_at = today;
      if (keys.has('author_name')) auto.author_name = user?.name || '';
      if (keys.has('name')) auto.name = user?.name || '';
      if (keys.has('dept')) auto.dept = user?.department || '';
      if (keys.has('position')) auto.position = user?.position || '';
      if (keys.has('enacted_date')) auto.enacted_date = selectedForm?.enacted_date || '';
      if (keys.has('rev_date')) auto.rev_date = selectedForm?.rev_date || '';
      setFieldValues(auto);
      const formId = doc.form_id;
      API.get(`/approval/forms/${formId}/auto-values`).then(res => {
        const vals = res.data?.values || {};
        const patch = {};
        Object.keys(vals).forEach(k => { if (keys.has(k)) patch[k] = vals[k]; });
        if (Object.keys(patch).length) setFieldValues(prev => ({ ...patch, ...prev }));
      }).catch(() => {});
    } else {
      setFieldValues({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.form_id]);

  const downloadFilledPdf = () => {
    if (!selectedForm) return;
    setPdfBusy(true);
    API.post(`/approval/forms/${selectedForm.id}/layout/pdf-filled`,
      { title: doc.title || selectedForm.name, field_values: fieldValues },
      { responseType: 'blob' }
    ).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc.title || selectedForm.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    }).catch(() => alert('PDF 생성에 실패했습니다.')).finally(() => setPdfBusy(false));
  };

  const addLine = () => {
    if (!pickUser) return;
    const u = users.find(x => String(x.id) === String(pickUser));
    if (!u) return;
    setLines(ls => [...ls, { approver_id: u.id, approver_name: u.name, role: pickRole }]);
    setPickUser('');
  };
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  const moveLine = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= lines.length) return;
    const arr = [...lines]; [arr[idx], arr[ni]] = [arr[ni], arr[idx]]; setLines(arr);
  };
  const setSlotUser = (idx, userId) => setSlotUsers(s => ({ ...s, [idx]: userId }));

  // 회의록형 결재라인 고정 슬롯 → 실제 결재선 배열로 변환. 마지막 단계(보통 "승인")만 최종
  // 결재(결재)로, 그 앞 단계들(검토/부서장 등)은 합의로 저장한다.
  const buildSlotLines = () => {
    if (!reviewSteps) return [];
    return reviewSteps.map((label, idx) => {
      const u = users.find(x => String(x.id) === String(slotUsers[idx]));
      if (!u) return null;
      return { approver_id: u.id, approver_name: u.name, role: idx === reviewSteps.length - 1 ? '결재' : '합의' };
    }).filter(Boolean);
  };

  const resetForm = () => { setDoc(blank); setLines([]); setSlotUsers({}); setCircType(''); setCircDept(''); setFieldValues({}); };

  const save = (submit) => {
    if (!doc.title.trim()) return alert('제목을 입력해주세요.');
    const useSlots = isMeetingTpl && reviewSteps && reviewSteps.length;
    const finalLines = useSlots ? buildSlotLines() : lines;
    if (submit && useSlots && finalLines.length !== reviewSteps.length) {
      return alert('결재라인의 모든 단계에 결재자를 지정해주세요.');
    }
    if (submit && !useSlots && !finalLines.length && !(isMeetingTpl && circType === '협조')) {
      return alert('결재선을 1명 이상 지정해주세요.');
    }
    if (submit && isMeetingTpl && circType && !circDept) return alert('회람/협조 부서를 선택해주세요.');

    API.post('/approval/documents', {
      form_id: doc.form_id || null, form_name: selectedForm ? selectedForm.name : null,
      title: doc.title, content: doc.content, amount: isMeetingTpl ? null : (doc.amount || null),
      department: doc.department, lines: finalLines, submit,
      circulation_type: isMeetingTpl ? (circType || null) : null,
      circulation_department: isMeetingTpl ? (circDept || null) : null,
      field_values: customLayout ? fieldValues : undefined,
    }).then(() => { showMsg(submit ? '상신되었습니다.' : '임시저장되었습니다.'); resetForm(); onSubmitted(submit); })
      .catch(err => alert(err.response?.data?.message || '저장 실패'));
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1.3fr 1fr', gap:16 }}>
      <Section title={isMeetingTpl ? undefined : '문서 작성'}>
        {isMeetingTpl && (
          <div style={{ textAlign:'center', fontSize:22, fontWeight:700, letterSpacing:2, padding:'4px 0 16px', borderBottom:`2px solid ${C.text}`, marginBottom:18 }}>
            {spacedTitle(selectedForm.name)}
          </div>
        )}
        <Field label="문서양식">
          <select style={{ ...input, width:'100%' }} value={doc.form_id} onChange={e => setDoc(d => ({ ...d, form_id:e.target.value }))}>
            <option value="">양식 선택(선택사항)</option>
            {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="제목"><input style={{ ...input, width:'100%' }} value={doc.title} onChange={e => setDoc(d => ({ ...d, title:e.target.value }))} /></Field>
        <div style={{ display:'flex', gap:8 }}>
          <Field label="부서"><input style={{ ...input, width:'100%' }} value={doc.department} onChange={e => setDoc(d => ({ ...d, department:e.target.value }))} /></Field>
          {!isMeetingTpl && (
            <Field label="금액(선택)"><input type="number" style={{ ...input, width:'100%' }} value={doc.amount} onChange={e => setDoc(d => ({ ...d, amount:e.target.value }))} /></Field>
          )}
        </div>
        {isMeetingTpl && (
          <Field label="업무회람 / 업무협조(선택)">
            <div style={{ display:'flex', gap:8 }}>
              <select style={{ ...input, width:140 }} value={circType}
                onChange={e => { setCircType(e.target.value); if (!e.target.value) setCircDept(''); }}>
                <option value="">선택 안 함</option>
                <option value="회람">업무회람</option>
                <option value="협조">업무협조</option>
              </select>
              {circType && (
                <select style={{ ...input, flex:1 }} value={circDept} onChange={e => setCircDept(e.target.value)}>
                  <option value="">부서 선택</option>
                  {depts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
            {circType === '회람' && <div style={{ fontSize:11, color:'#888', marginTop:6 }}>선택한 부서의 부서장(팀장, 없으면 하위직급)에게 회람/참조로 전달됩니다.</div>}
            {circType === '협조' && <div style={{ fontSize:11, color:'#888', marginTop:6 }}>선택한 부서의 부서장이 결재선 맨 앞(작성 다음)에 검토자로 자동 추가됩니다.</div>}
          </Field>
        )}
        {customLayout ? (
          <Field label="양식 내용">
            <FillableLayoutTable layout={customLayout} fieldValues={fieldValues} setFieldValues={setFieldValues} />
            <button type="button" onClick={downloadFilledPdf} disabled={pdfBusy}
              style={{ ...btn('#f0f0f0', '#555'), marginTop:10, width:'100%', opacity: pdfBusy ? 0.6 : 1 }}>
              {pdfBusy ? 'PDF 생성 중...' : '📄 입력내용으로 서식 미리보기(PDF 다운로드)'}
            </button>
          </Field>
        ) : (
          <Field label={isMeetingTpl ? '전달사항' : '내용'}>
            <textarea rows={8} style={{ ...input, width:'100%', resize:'vertical' }} value={doc.content} onChange={e => setDoc(d => ({ ...d, content:e.target.value }))} />
          </Field>
        )}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => save(false)} style={{ ...btn('#f0f0f0', '#555'), flex:1 }}>임시저장</button>
          <button onClick={() => save(true)} style={{ ...btn(C.primary), flex:2 }}>상신하기</button>
        </div>
      </Section>

      <Section title="결재선 지정">
        {isMeetingTpl && reviewSteps && reviewSteps.length ? (
          <div>
            <div style={{ fontSize:11, color:'#aaa', marginBottom:12 }}>이 양식에 지정된 결재라인입니다. 각 단계별로 결재자를 선택해주세요.</div>
            {reviewSteps.map((label, idx) => (
              <div key={idx} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <span style={{ width:56, flexShrink:0, fontSize:12, fontWeight:700, color:C.primary }}>{label}</span>
                <UserSearchSelect users={users.filter(u => u.id !== user?.id)} value={slotUsers[idx] || ''} onChange={v => setSlotUser(idx, v)} />
              </div>
            ))}
            {circType === '협조' && circDept && (
              <div style={{ fontSize:12, color:'#7c3aed', background:'#f3e8ff', padding:'8px 10px', borderRadius:8, marginTop:10 }}>
                🤝 {circDept} 부서장이 "작성" 다음 결재선에 자동 추가됩니다.
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <UserSearchSelect users={users.filter(u => u.id !== user?.id)} value={pickUser} onChange={setPickUser} />
              <select style={{ ...input, width:90 }} value={pickRole} onChange={e => setPickRole(e.target.value)}>
                <option value="결재">결재</option><option value="합의">합의</option><option value="참조">참조</option>
              </select>
              <button onClick={addLine} style={btn(C.primary)}>추가</button>
            </div>

            {lines.length === 0 ? (
              <div style={{ textAlign:'center', color:'#aaa', fontSize:13, padding:'24px 0' }}>결재선이 지정되지 않았습니다.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {lines.map((l, idx) => {
                  const rc = roleColor[l.role];
                  return (
                    <div key={idx} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:8 }}>
                      <span style={{ fontSize:12, color:'#999', width:18 }}>{idx + 1}</span>
                      <span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:rc.bg, color:rc.c }}>{l.role}</span>
                      <span style={{ flex:1, fontSize:13, fontWeight:600 }}>{l.approver_name}</span>
                      <button disabled={idx===0} onClick={() => moveLine(idx, -1)} style={{ background:'none', border:'none', cursor: idx===0?'default':'pointer', color: idx===0?'#ddd':'#888', fontSize:11 }}>▲</button>
                      <button disabled={idx===lines.length-1} onClick={() => moveLine(idx, 1)} style={{ background:'none', border:'none', cursor: idx===lines.length-1?'default':'pointer', color: idx===lines.length-1?'#ddd':'#888', fontSize:11 }}>▼</button>
                      <button onClick={() => removeLine(idx)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', fontWeight:600 }}>제거</button>
                    </div>
                  );
                })}
              </div>
            )}
            {isMeetingTpl && circType === '협조' && circDept && (
              <div style={{ fontSize:12, color:'#7c3aed', background:'#f3e8ff', padding:'8px 10px', borderRadius:8, marginTop:10 }}>
                🤝 {circDept} 부서장이 "작성" 다음 결재선에 자동 추가됩니다.
              </div>
            )}
            <div style={{ fontSize:11, color:'#aaa', marginTop:12 }}>※ 순서대로 결재/합의 대상자에게 전달됩니다. 같은 순번은 함께 표시할 수 없어 한 줄씩 순서가 매겨집니다. 참조는 순서와 무관하게 열람만 가능합니다.</div>
          </>
        )}
      </Section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 결재완료 / 결재대기 / 회람·참조 / 결재반려 — 공통 목록 컴포넌트
// (각 탭은 fetchRows 함수만 다르게 넘겨서 재사용한다. canViewAll 권한자에게는
//  "전체문서함" 체크박스가 추가로 표시된다.)
// ═══════════════════════════════════════════════════════════════
function fetchDocBox(box) { return API.get('/approval/documents', { params: { box } }).then(r => r.data); }

function DocListTab({ title, emptyText, fetchRows, allToggleLabel, user, canViewAll, showMsg, openDocId, setOpenDocId }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = () => fetchRows(showAll).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const kw = search.trim();
  const filtered = !kw ? rows : rows.filter(d =>
    (d.title || '').includes(kw) || (d.drafter_name || '').includes(kw) ||
    (d.department || '').includes(kw) || (d.form_name || '').includes(kw));
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);

  return (
    <Section title={title}
      action={
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 제목, 기안자, 부서 검색..."
            style={{ ...input, width:200, padding:'7px 12px', fontSize:13 }} />
          {canViewAll && (
            <label style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, color:'#666', cursor:'pointer', whiteSpace:'nowrap' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> {allToggleLabel || '전체문서함'}
            </label>
          )}
        </div>
      }>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <SortTh label="제목" sortKey="title" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="left" />
            <SortTh label="양식" sortKey="form_name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <SortTh label="기안자" sortKey="drafter_name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <SortTh label="부서" sortKey="department" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <th style={th}>기록물번호</th>
            <th style={th}>금액</th>
            <th style={th}>상태</th>
            <SortTh label="작성일" sortKey="created_at" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          </tr></thead>
          <tbody>
            {sorted.length === 0 && <EmptyRow span={8} text={kw ? '검색 결과가 없습니다.' : emptyText} />}
            {sorted.map(d => (
              <tr key={d.id} onClick={() => setOpenDocId(d.id)} style={{ cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...td, textAlign:'left', fontWeight:600 }}>{d.title}</td><td style={td}>{d.form_name || '-'}</td>
                <td style={td}>{d.drafter_name}</td><td style={td}>{d.department || '-'}</td>
                <td style={{ ...td, fontFamily:'monospace', fontSize:11 }}>{d.record_no || '-'}</td>
                <td style={{ ...td, textAlign:'right' }}>{d.amount ? Number(d.amount).toLocaleString() + '원' : '-'}</td>
                <td style={td}><StatusBadge s={d.status} /></td>
                <td style={td}>{(d.created_at || '').slice(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openDocId && (
        <DocumentDetailModal id={openDocId} user={user} onClose={() => setOpenDocId(null)}
          onChanged={() => { load(); showMsg('처리되었습니다.'); }} />
      )}
    </Section>
  );
}

// 결재완료 — 최종 승인된 문서
function DoneTab(props) {
  const fetchRows = (all) => fetchDocBox(all ? 'all' : 'done').then(rows => rows.filter(d => d.status === '승인'));
  return <DocListTab {...props} title="결재완료" emptyText="완료된 문서가 없습니다." fetchRows={fetchRows} />;
}

// 결재대기 — 내가 결재/합의할 차례인 문서 + 내가 상신해서 아직 진행중인 문서(구 상신함)
function PendingTab(props) {
  const fetchRows = (all) => {
    if (all) return fetchDocBox('all').then(rows => rows.filter(d => d.status === '진행중'));
    return Promise.all([fetchDocBox('pending-me'), fetchDocBox('my-drafts')]).then(([pending, mine]) => {
      const inProgress = mine.filter(d => d.status === '진행중');
      const map = new Map();
      [...pending, ...inProgress].forEach(d => map.set(d.id, d));
      return Array.from(map.values()).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    });
  };
  return <DocListTab {...props} title="결재대기" emptyText="결재대기 중인 문서가 없습니다." fetchRows={fetchRows} />;
}

// 회람/참조 — 참조로 지정된 문서
function ReferenceTab(props) {
  const fetchRows = (all) => fetchDocBox(all ? 'all' : 'reference-me');
  return <DocListTab {...props} title="회람/참조" emptyText="회람·참조로 지정된 문서가 없습니다." fetchRows={fetchRows} />;
}

// 결재반려 — 내가 기안한 문서 중 반려된 것 + 내가 결재자로서 반려처리한 문서
function RejectedTab(props) {
  const fetchRows = (all) => fetchDocBox(all ? 'all' : 'rejected').then(rows => rows.filter(d => d.status === '반려'));
  return <DocListTab {...props} title="결재반려" emptyText="반려된 문서가 없습니다." fetchRows={fetchRows} />;
}

function DocumentDetailModal({ id, user, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [comment, setComment] = useState('');

  const load = () => API.get(`/approval/documents/${id}`).then(r => setDoc(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  if (!doc) return <Modal title="문서 상세" onClose={onClose}><div style={{ padding:20, textAlign:'center', color:'#aaa' }}>불러오는 중...</div></Modal>;

  const myLine = doc.lines.find(l => l.approver_id === user?.id && l.status === '대기' && l.step_order === doc.current_step && (l.role === '결재' || l.role === '합의'));
  const isDrafter = doc.drafter_id === user?.id;

  const decide = (decision) => {
    if (decision === '반려' && !window.confirm('반려하시겠습니까?')) return;
    API.patch(`/approval/documents/${id}/decide`, { line_id: myLine.id, decision, comment })
      .then(() => { onChanged(); load(); setComment(''); })
      .catch(err => alert(err.response?.data?.message || '처리 실패'));
  };
  // 서식(레이아웃) 양식으로 작성된 문서는 입력값(field_values_json)으로 서식 PDF를 열어 볼 수 있다.
  let savedValues = null;
  if (doc.field_values_json) { try { savedValues = JSON.parse(doc.field_values_json); } catch { savedValues = null; } }
  const viewFilledPdf = () => {
    API.post(`/approval/forms/${doc.form_id}/layout/pdf-filled`, { title: doc.title, field_values: savedValues }, { responseType: 'blob' })
      .then(res => { window.open(window.URL.createObjectURL(new Blob([res.data], { type:'application/pdf' })), '_blank'); })
      .catch(() => alert('서식 PDF를 불러오지 못했습니다.'));
  };
  const submit = () => API.patch(`/approval/documents/${id}/submit`).then(() => { onChanged(); load(); }).catch(err => alert(err.response?.data?.message || '상신 실패'));
  const recall = () => { if (!window.confirm('회수하시겠습니까?')) return;
    API.patch(`/approval/documents/${id}/recall`).then(() => { onChanged(); load(); }).catch(err => alert(err.response?.data?.message || '회수 실패')); };
  const remove = () => { if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/approval/documents/${id}`).then(() => { onChanged(); onClose(); }).catch(err => alert(err.response?.data?.message || '삭제 실패')); };

  return (
    <Modal title={`📄 ${doc.title}`} onClose={onClose} width={620}>
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <StatusBadge s={doc.status} />
        <span style={{ fontSize:13, color:'#666' }}>{doc.form_name || '일반문서'}</span>
        <span style={{ fontSize:13, color:'#666' }}>· 기안자 {doc.drafter_name} ({doc.department || '-'})</span>
        {doc.amount && <span style={{ fontSize:13, color:'#666' }}>· {Number(doc.amount).toLocaleString()}원</span>}
        {doc.record_no && (
          <span style={{ fontSize:12, fontFamily:'monospace', fontWeight:700, color:C.primary, background:'#e8f4ff', padding:'2px 8px', borderRadius:6 }}>
            기록물번호 {doc.record_no}
          </span>
        )}
      </div>
      <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:14, marginBottom:16, fontSize:13, whiteSpace:'pre-wrap', minHeight:80 }}>
        {doc.content || '(내용 없음)'}
      </div>
      {savedValues && doc.form_id && (
        <button onClick={viewFilledPdf} style={{ ...btn('#f0f0f0', '#555'), width:'100%', marginBottom:16 }}>📄 작성된 서식 보기(PDF)</button>
      )}

      <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>결재선</div>
      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:16 }}>
        {doc.lines.length === 0 && <div style={{ fontSize:12, color:'#aaa' }}>지정된 결재선이 없습니다.</div>}
        {doc.lines.map(l => {
          const rc = roleColor[l.role];
          const active = doc.status === '진행중' && l.step_order === doc.current_step && l.status === '대기' && (l.role === '결재' || l.role === '합의');
          return (
            <div key={l.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
              border: active ? `1.5px solid ${C.primary}` : `1px solid ${C.border}`, borderRadius:8, background: active ? '#f0f7ff' : '#fff' }}>
              <span style={{ fontSize:12, color:'#999', width:18 }}>{l.step_order}</span>
              <span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:rc.bg, color:rc.c }}>{l.role}</span>
              <span style={{ flex:1, fontSize:13, fontWeight:600 }}>{l.approver_name}</span>
              <StatusBadge s={l.status === '대기' ? (active ? '진행중' : '대기') : l.status} />
              {l.comment && <span style={{ fontSize:12, color:'#888' }}>“{l.comment}”</span>}
            </div>
          );
        })}
      </div>

      {myLine && (
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          <Field label="결재 의견(선택)"><textarea rows={2} style={{ ...input, width:'100%', resize:'vertical' }} value={comment} onChange={e => setComment(e.target.value)} /></Field>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => decide('반려')} style={{ ...btn('#dc2626'), flex:1 }}>반려</button>
            <button onClick={() => decide('승인')} style={{ ...btn('#16a34a'), flex:1 }}>승인</button>
          </div>
        </div>
      )}

      {isDrafter && doc.status === '임시저장' && (
        <div style={{ display:'flex', gap:8, borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          <button onClick={remove} style={{ ...btn('#f0f0f0', '#dc2626'), flex:1 }}>삭제</button>
          <button onClick={submit} style={{ ...btn(C.primary), flex:2 }}>상신하기</button>
        </div>
      )}
      {isDrafter && doc.status === '진행중' && !doc.lines.some(l => l.status === '승인' || l.status === '반려') && (
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          <button onClick={recall} style={{ ...btn('#f0f0f0', '#555'), width:'100%' }}>회수</button>
        </div>
      )}
      {isDrafter && doc.status === '회수' && (
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          <button onClick={remove} style={{ ...btn('#f0f0f0', '#dc2626'), width:'100%' }}>삭제</button>
        </div>
      )}
    </Modal>
  );
}

// 참고: 위임관리(대결) 화면은 이번 개편에서 화면 구성에서 제외했습니다.
// 데이터(approval_delegations)와 백엔드 API(/api/approval/delegations)는 그대로 남아있어
// 필요 시 언제든 화면을 다시 추가할 수 있습니다.
